import type { WithID } from '@ps/models/lib/base';
import type { Profile } from '@ps/models/lib/profile';

/** Profile fields dropped from {@link ProfileSlim} — none are read by the slim
 *  consumers, and each is dead weight in the cache / Server→Client payload:
 *  `customization` (the large serialized-bio JSON), the `createdAt`/`updatedAt`
 *  timestamps, and the `badges` / `hideBadgeInProfile` / `apps` metadata. */
type SlimmedProfileField =
  | 'customization'
  | 'createdAt'
  | 'updatedAt'
  | 'badges'
  | 'hideBadgeInProfile'
  | 'apps';

/**
 * A creator {@link Profile} trimmed to its identity / display fields (url code,
 * user type, name / bio / avatar) — see {@link SlimmedProfileField} for what's
 * dropped. Consumers that never touch the heavy / unused fields declare this so
 * neither the cache entry nor a Server→Client payload carries them. Pair with
 * `getSlimProfileByUserIdCached` / `profileSlimmed`.
 *
 * `WithID` is baked in (the doc id always travels with a slim profile), so
 * consumers write `ProfileSlim`, never `WithID<ProfileSlim>`.
 *
 * `urlCode` is **required** here (unlike the optional `Profile.urlCode`): a slim
 * profile is only ever built for a creator, and the bio-render consumers read
 * `profile.urlCode` as a plain `string`. A non-creator / unpublished profile
 * gets an empty-string `urlCode` (`profileSlimmed` coerces it) — harmless, since
 * those profiles never reach a urlCode-dependent surface.
 */
export type ProfileSlim = WithID<
  Omit<Profile, SlimmedProfileField> & {
    urlCode: string;
  }
>;

/**
 * Trim a full profile to a {@link ProfileSlim} — drop the heavy / unused fields
 * (see {@link SlimmedProfileField}) before it is cached for a slim consumer or
 * crosses to a Client Component. Pure / sync. Also removes the
 * `createdAt`/`updatedAt` timestamps, so a slim profile carries no
 * Firestore-Timestamp-derived values at all. A profile without a `urlCode` (a
 * non-creator / unpublished account) gets an empty-string one — harmless, since
 * such profiles never reach a urlCode-dependent surface.
 */
export function profileSlimmed(profile: WithID<Profile>): ProfileSlim {
  const {
    customization: _customization,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    badges: _badges,
    hideBadgeInProfile: _hideBadgeInProfile,
    apps: _apps,
    ...rest
  } = profile;
  return { ...rest, urlCode: rest.urlCode ?? '' };
}
